import { Nullable } from "util/extendedTypes";
import {
  composeValidation,
  maximumCharacterLimit,
  notNull,
  stringEmpty,
  validateObject,
  validateRange,
  ValidationFunc,
} from "validation/validation";

import {
  REMAINING_BAL_2_DECIMAL_VALIDATION_ERROR_MSG,
  REMAINING_BAL_MIN_MAX_DECIMAL_VALIDATION_ERROR_MSG,
} from "../components/AboutYourLoan/YourLoan.const";

export type AboutYourLoan = {
  accountNumber: string;
  sortCode: string;
  remainingBalance: number;
  treatmentTaken: Nullable<boolean>;
  CRHTaken: Nullable<number>;
  CIRHTaken: Nullable<number>;
  fullTermExtensionTaken: boolean;
  finalRepaymentDate: Nullable<Date>;
  CRHWithTermExtensionCountTaken: Nullable<number>;
  CIRHWithTermExtensionCountTaken: Nullable<number>;
};

export const ZERO = 0;
export const ONE = 1;
export const TWO = 2;
export const THREE = 3;

export const defaultAboutYourLoan = (): AboutYourLoan => {
  return {
    accountNumber: "",
    sortCode: "",
    remainingBalance: 0,
    treatmentTaken: null,
    CRHTaken: null,
    CIRHTaken: null,
    fullTermExtensionTaken: false,
    finalRepaymentDate: null,
    CRHWithTermExtensionCountTaken: null,
    CIRHWithTermExtensionCountTaken: null,
  };
};

export const accountNameValidation = composeValidation(
  stringEmpty("Please enter your account name"),
  maximumCharacterLimit(255, "Maximum 255 characters")
);

const accountNumberFormatValidation: ValidationFunc<string> = (s: string) =>
  /^\d{8}$/.test(s) ? [] : [`Please enter a valid account number`];

export const accountNumberValidation = composeValidation(
  stringEmpty("Please enter your account number"),
  accountNumberFormatValidation
);

export const accountSortCodeFormatValidation: ValidationFunc<string> = (
  s: string
) => (/^\d{6}$/.test(s) ? [] : [`Please enter a valid sort code`]);

export const sortCodeValidation = composeValidation(
  stringEmpty("Please enter your sort code"),
  accountSortCodeFormatValidation
);

export const finalRepaymentDateValidation = notNull(
  "Please enter the final repayment date in the format shown above or select the date using the calendar provided. For help with understanding your final repayment date, please refer to the information tooltip above."
);

const remainingBalance2DecimalPlacesValidation: ValidationFunc<number> = (
  n: number
) =>
  /^(?:\d*\.\d{1,2}|\d+)$/.test(n.toString())
    ? []
    : [REMAINING_BAL_2_DECIMAL_VALIDATION_ERROR_MSG];

export const remainingBalanceValidation = composeValidation<Nullable<number>>(
  notNull("Please enter the balance of your loan"),
  remainingBalance2DecimalPlacesValidation as ValidationFunc<Nullable<number>>,
  validateRange(
    0.01,
    50000,
    REMAINING_BAL_MIN_MAX_DECIMAL_VALIDATION_ERROR_MSG
  ) as ValidationFunc<Nullable<number>>
);

export const treatmentTakenValidation = notNull<boolean>(
  "Please answer treatment taken or not"
);

export const crhCountValidation = notNull<number>(
  "Please enter the no of CRH holidays taken"
);

export const cirhCountValidation = notNull<number>(
  "Please enter the no of CIRH holidays taken"
);

export const CRHWithTermExtensionCountTaken = notNull<number>(
  "Please enter the no of CRH extension taken"
);

export const CIRHWithTermExtensionCountTaken = notNull<number>(
  "Please enter the no of CIRH extension taken"
);

export const treatmentValidation = (aboutYourLoan: AboutYourLoan) =>
  aboutYourLoan.treatmentTaken
    ? validateObject<AboutYourLoan>({
        CRHTaken: crhCountValidation,
        CIRHTaken: cirhCountValidation,
        CRHWithTermExtensionCountTaken: CRHWithTermExtensionCountTaken,
        CIRHWithTermExtensionCountTaken: CIRHWithTermExtensionCountTaken,
      })(aboutYourLoan)
    : [];
export const validateAboutYourLoan = composeValidation(
  validateObject<AboutYourLoan>({
    accountNumber: accountNumberValidation,
    sortCode: sortCodeValidation,
    remainingBalance: remainingBalanceValidation,
    finalRepaymentDate: finalRepaymentDateValidation,
    treatmentTaken: treatmentTakenValidation,
  }),
  treatmentValidation
);
